<template>
  <Modal
    v-model:visible="isVisible"
    class="manifest-modal"
    content-class="flex flex-column flex-gap-1"
    :header="t('manifests.createManifest')"
    size="lg"
  >
    <template #default>
      <MessageContainer
        :messages="modalMessages"
        message-class="rygen-message"
      />

      <div class="flex flex-row flex-gap-1">
        <InputWrapper
          v-if="hasSubdomains"
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${uid}-domain-select`"
          :label="t('clients.clients')"
          class="w-4"
        >
          <ClientDropdown
            :id="inputId"
            v-model="selectedClient"
            v-bind="ariaProps"
            :class="inputClasses"
            :clients="subdomains"
            append-to="body"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${uid}-carrier-select`"
          :label="t('carriers.carriers')"
          class="w-4"
        >
          <CarrierDropdown
            :id="inputId"
            v-model="selectedCarrier"
            v-bind="ariaProps"
            :class="inputClasses"
            :carriers="carriers"
            append-to="body"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ inputId }"
          :input-id="`${uid}-pickup-location-select`"
          :label="t('parcelScheduling.pickupLocations')"
          class="w-4"
        >
          <ParcelPickupLocationQuickSearch
            :id="inputId"
            v-model="selectedLocation"
            append-to="body"
            :placeholder="t('parcelScheduling.pickupLocations')"
          />
        </InputWrapper>
        <InputWrapper
          v-slot="{ inputId }"
          :input-id="`${uid}-date-select`"
          :label="
            t('common.paramDates', {
              param: t('manifests.manifests'),
              count: 1,
            })
          "
        >
          <RygenCalendar
            :id="inputId"
            v-model="manifestDate"
            :time-zone="timeZone"
            :placeholder="
              t('common.paramDates', {
                param: t('manifests.manifests'),
                count: 1,
              })
            "
          />
        </InputWrapper>
      </div>

      <ShipmentSearchResultView
        v-if="hasRequiredValues"
        v-model:columns="columns"
        :selection="pageSelection"
        :shipment-search-criteria="criteria"
        :results="results"
        @update:selection="onSelectionChange"
        @page="onPage"
        @sort="onSort"
        @refresh-no-cache="runPreviewDebounced"
      />
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-file"
        :label="t('manifests.createManifest')"
        :disabled="selectedCount < 1"
        :loading="sendResponse.loading"
        @click="onSend"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { default as axios, CancelTokenSource } from 'axios';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import { computed, h, onMounted, ref, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useUid } from '@rygen/rygen-ui/composables';
import {
  ColumnSpecification,
  DomainReference,
  DomainTree,
  DomainType,
  Loadable,
  Location,
  Mode,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { ManifestCriteria } from '@/api/controller/ShipmentManifestController';
import {
  Carrier,
  ManifestError,
  ManifestErrorResponse,
  ManifestResponse,
  MessageSpecification,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  Shipment,
  ShipmentSearchCriteria,
  ShipmentSearchData,
  ShipmentSearchResult,
  WEIGHT_COLUMN,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import ParcelPickupLocationQuickSearch from '@/components/shipment/parcel/ParcelPickupLocationQuickSearch.vue';
import ShipmentSearchResultView from '@/components/shipment/ShipmentSearchResult.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CarrierDropdown from '@rygen/rygen-ui/components/form/CarrierDropdown.vue';
import ClientDropdown from '@rygen/rygen-ui/components/form/ClientDropdown.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

const SEARCH_DELAY = 500;
const PAGE_SIZE = 20;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'success', result: ManifestResponse): void;
}>();

const { availableDomainMap, selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const toast = useToast();
const { uid } = useUid('manifest-modal');

const carriers: Ref<Carrier[]> = ref([]);

const columns: Ref<ColumnSpecification[]> = ref([
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SERVICE_COLUMN,
  WEIGHT_COLUMN,
]);

const scac: Ref<string | undefined> = ref();
const ownerNaturalId: Ref<string | undefined> = ref();
const selectedLocation: Ref<Location | undefined> = ref();
const manifestDate: Ref<DateTime | undefined> = ref();
// Shipments are all selected by default; we track only the ids the operator explicitly deselects, so the selection
// persists across pages and the send can send "everything eligible except these".
const deselectedIds: Ref<Set<number>> = ref(new Set());

const results: Ref<Loadable<ShipmentSearchResult>> = ref({
  loading: false,
  data: new ShipmentSearchResult(),
});
const sendResponse: Ref<Loadable<ManifestResponse>> = ref({ loading: false });

const criteria: Ref<ShipmentSearchCriteria> = ref(
  new ShipmentSearchCriteria({
    modes: ['PARCEL'],
    page: 0,
    pageSize: PAGE_SIZE,
  }),
);

const cancelRequest: Ref<CancelTokenSource | undefined> = ref();

const selectedCarrier = computed({
  get() {
    return carriers.value.find((c) => c.scac === scac.value);
  },
  set(carrier: Carrier | undefined) {
    scac.value = carrier?.scac;
  },
});

const subdomains = computed(() =>
  getDomainAndDescendantsForDomain(selectedDomain.value).filter(
    (d) => d.domainType === DomainType.Client,
  ),
);

const selectedClient = computed({
  get() {
    return subdomains.value.find((d) => d.naturalId === ownerNaturalId.value);
  },
  set(client: DomainReference | undefined) {
    ownerNaturalId.value = client?.naturalId;
  },
});

const hasSubdomains = computed(() =>
  selectedDomain.value?.domainType === DomainType.Client
    ? subdomains.value.length > 1
    : subdomains.value.length > 0,
);

const timeZone = computed(() => selectedLocation.value?.timeZone ?? 'UTC-5');

const hasRequiredValues = computed(() => {
  if (hasSubdomains.value && !ownerNaturalId.value) {
    return false;
  }
  return !!scac.value && !!selectedLocation.value && !!manifestDate.value;
});

const pageRows = computed(() => results.value.data?.content ?? []);

const totalEligible = computed(() => results.value.data?.totalElements ?? 0);

const selectedCount = computed(
  () => totalEligible.value - deselectedIds.value.size,
);

const pageSelection = computed(() =>
  pageRows.value.filter((s) => s.id != null && !deselectedIds.value.has(s.id)),
);

const currentCriteria = computed((): ManifestCriteria => ({
  scac: scac.value as string,
  originLocationId: selectedLocation.value?.id,
  manifestDate: manifestDate.value?.setZone(timeZone.value, {
    keepLocalTime: true,
  }) as DateTime,
  ownerNaturalId: ownerNaturalId.value,
}));

const manifestErrors = computed((): ManifestError[] => {
  return extractProviderErrors(sendResponse.value.error);
});

const manifestErrorMessages = computed((): MessageSpecification[] => {
  return manifestErrors.value.map((e, index) => ({
    id: `manifest-error-${index}`,
    textSlot: () => h('span', { class: 'font-bold' }, e.message),
    severity: 'error',
  }));
});

const modalMessages = computed((): MessageSpecification[] => {
  if (!hasRequiredValues.value) {
    return manifestErrorMessages.value;
  }
  return [
    {
      id: 'manifest-warning',
      textSlot: () => h('span', t('manifests.warning')),
      severity: 'warn',
    },
    ...manifestErrorMessages.value,
  ];
});

const getDomainAndDescendantsForDomain = (
  currentDomain: DomainReference | undefined,
) => {
  const recursiveFlatMap = (domainTree: DomainTree): DomainReference[] => {
    return [
      domainTree.asDomainReference(),
      ...domainTree.children.flatMap(recursiveFlatMap),
    ];
  };

  if (!currentDomain?.id) {
    return [];
  }

  const currentDomainTree = availableDomainMap.value.get(currentDomain.id);

  if (currentDomainTree == null) {
    return [];
  }

  return recursiveFlatMap(currentDomainTree);
};

const extractProviderErrors = (error: unknown): ManifestError[] => {
  if (error == null) {
    return [];
  }

  if (!axios.isAxiosError(error)) {
    return (error as ManifestError[])?.map((e) => ({
      message: e.message ?? 'Unknown Error',
    }));
  }

  const errorData = error.response?.data as { errorDetails?: unknown };
  const errorResponse = new ManifestErrorResponse(
    errorData?.errorDetails as Partial<ManifestErrorResponse>,
  );
  return errorResponse.errors.filter((x) => x?.message);
};

const loadCarriers = async () => {
  try {
    const allCarriers = await api.carrier.findAll();
    carriers.value = allCarriers.filter(
      (c) => c.modes.includes(Mode.PARCEL) && c.manifestingEnabled,
    );
  } catch (e) {
    console.error('Error loading carriers', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('carriers.carriers', 2) }),
      error: e,
    });
  }
};

const runPreview = async () => {
  if (!hasRequiredValues.value) {
    return;
  }

  cancelRequest.value?.cancel();
  cancelRequest.value = axios.CancelToken.source();

  try {
    results.value.loading = true;
    results.value.data = await api.shipmentManifest.previewManifest(
      currentCriteria.value,
      {
        page: criteria.value.page ?? 0,
        pageSize: criteria.value.pageSize ?? PAGE_SIZE,
        sortField: criteria.value.sortField as string | undefined,
        sortDirection: criteria.value.sortDirection,
      },
      cancelRequest.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error gathering manifest shipments', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('shipments.shipments', 2) }),
      error: e,
    });
  } finally {
    results.value.loading = false;
  }
};

const runPreviewDebounced = debounce(() => runPreview(), SEARCH_DELAY);

const onSelectionChange = (selected: ShipmentSearchData[]) => {
  const selectedIds = new Set(selected.map((s) => s.id));
  const next = new Set(deselectedIds.value);
  pageRows.value.forEach((row) => {
    if (row.id == null) {
      return;
    }
    if (selectedIds.has(row.id)) {
      next.delete(row.id);
    } else {
      next.add(row.id);
    }
  });
  deselectedIds.value = next;
};

const onSend = async () => {
  if (!hasRequiredValues.value || selectedCount.value < 1) {
    return;
  }

  try {
    sendResponse.value.loading = true;
    sendResponse.value.error = undefined;
    sendResponse.value.data = await api.shipmentManifest.sendManifest(
      currentCriteria.value,
      [...deselectedIds.value],
    );
    if (!sendResponse.value.data.success) {
      sendResponse.value.error = sendResponse.value.data.errors;
      toast.addError({
        detail: t('errors.creatingParam', {
          param: t('manifests.manifests', 1),
        }),
        error: undefined,
      });
    } else {
      toast.addSuccess({
        detail: t('messages.successCreatedParam', {
          param: t('manifests.manifests', 1),
        }),
      });
      emit('success', sendResponse.value.data);
      isVisible.value = false;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error creating manifest', e);
    sendResponse.value.error = e;
    toast.addError({
      detail: t('errors.creatingParam', {
        param: t('manifests.manifests', 1),
      }),
      error: e,
    });
  } finally {
    sendResponse.value.loading = false;
  }
};

const onPage = (event: DataTablePageEvent) => {
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
  runPreview();
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Shipment | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
  runPreview();
};

const resetFields = () => {
  scac.value = undefined;
  ownerNaturalId.value = undefined;
  selectedLocation.value = undefined;
  manifestDate.value = DateTime.now().plus({ days: 1 });
  deselectedIds.value = new Set();
  results.value = { loading: false, data: new ShipmentSearchResult() };
  sendResponse.value = { loading: false };
  criteria.value = new ShipmentSearchCriteria({
    modes: ['PARCEL'],
    page: 0,
    pageSize: PAGE_SIZE,
  });
};

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    resetFields();
  }
});

watch(
  [scac, ownerNaturalId, selectedLocation, manifestDate],
  () => {
    sendResponse.value.error = undefined;
    if (hasRequiredValues.value) {
      deselectedIds.value = new Set();
      criteria.value.page = 0;
      runPreviewDebounced();
    }
  },
  { deep: true },
);

onMounted(() => {
  loadCarriers();
});
</script>
